import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { usePageSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Zap, LogOut, LayoutGrid, List, BarChart3, Plus, Search, ArrowRight, Flame, Home, Users, Calendar, FileText, AlertTriangle, Bell, Eye, X, Pencil, ArrowLeft, Link2, ChevronDown, ChevronUp, Mail, Send, Loader2, Briefcase, Newspaper } from "lucide-react";
import OpsPipeline from "./ops/OpsPipeline";
import OpsLeadDetail from "./ops/OpsLeadDetail";
import OpsAnalytics from "./ops/OpsAnalytics";
import OpsSalesMaterial from "./ops/OpsSalesMaterial";
import OpsInsights from "./ops/OpsInsights";
import CloseReasonModal from "./ops/CloseReasonModal";
import { DecayAlerts, BookingsTable, SubmissionsTable } from "./ops/OpsPanelViews";
import { INDUSTRIES } from "../data/industries";
import { motion } from "framer-motion";

const PIPELINE_STATUSES = ["new", "contacted", "booked", "qualified", "proposal", "closed_won", "closed_lost"];
const STATUS_LABELS = { new: "New", contacted: "Contacted", booked: "Booked", qualified: "Qualified", proposal: "Proposal", closed_won: "Won", closed_lost: "Lost" };
const formatStatus = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: Home },
  { id: "pipeline", label: "Pipeline", icon: LayoutGrid },
  { id: "leads", label: "Leads", icon: Users },
  { id: "submissions", label: "Discoveries", icon: FileText },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "sales-material", label: "Sales Material", icon: Briefcase },
  { id: "insights", label: "Insights", icon: Newspaper },
];

export default function OpsPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  usePageSEO({ title: "Operations", noIndex: true });
  const [section, setSection] = useState("dashboard");
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", email: "", company: "", role: "", industry: "", sub_industry: "", source: "manual", website: "", business_description: "" });
  const [creating, setCreating] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", company: "", website: "", industry: "", payment_required: true, personal_message: "", package: "ultimate" });
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleForm, setRescheduleForm] = useState({ date: "", time_slot: "" });
  const [closeModal, setCloseModal] = useState(null); // { leadId, leadName, company, isWin }
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [subDetail, setSubDetail] = useState(null);
  const [subFilter, setSubFilter] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (search) params.append("search", search);
      const [leadsRes, dashRes, alertsRes] = await Promise.all([
        api.get(`/ops/leads?${params}`),
        api.get("/ops/dashboard"),
        api.get("/ops/alerts")
      ]);
      setLeads(leadsRes.data);
      setDashboard(dashRes.data);
      setAlerts(alertsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadSubmissions = async () => {
    try { const { data } = await api.get(`/ops/submissions${subFilter && subFilter !== "all" ? `?status=${subFilter}` : ""}`); setSubmissions(data); } catch (e) { console.error(e); }
  };
  const loadBookings = async () => {
    try { const { data } = await api.get("/ops/bookings"); setBookings(data); } catch (e) { console.error(e); }
  };
  const loadSubDetail = async (auditId) => {
    try { const { data } = await api.get(`/ops/submissions/${auditId}`); setSubDetail(data); setSelectedSubmission(auditId); } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (section === "submissions") { loadSubmissions(); setSelectedSubmission(null); setSubDetail(null); }
    if (section === "bookings") loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, subFilter]);

  const handleStatusChange = async (leadId, newStatus, closeData = null) => {
    // Intercept close statuses — show modal first
    if ((newStatus === "closed_won" || newStatus === "closed_lost") && !closeData) {
      const lead = leads.find(l => l.id === leadId);
      setCloseModal({ leadId, leadName: lead?.name || "", company: lead?.company || "", isWin: newStatus === "closed_won" });
      return;
    }
    try {
      await api.put(`/ops/leads/${leadId}`, { status: newStatus, ...closeData });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
    } catch (err) { console.error(err); }
  };

  const handleCloseConfirm = async (closeData) => {
    if (!closeModal) return;
    const newStatus = closeModal.isWin ? "closed_won" : "closed_lost";
    await handleStatusChange(closeModal.leadId, newStatus, closeData);
    setCloseModal(null);
    loadData();
  };

  const openLead = (leadId) => { setSelectedLeadId(leadId); setSection("detail"); };

  const handleCreate = async () => {
    if (!newLead.name || !newLead.email) return;
    setCreating(true);
    try {
      await api.post("/ops/leads", newLead);
      setShowCreateModal(false);
      setNewLead({ name: "", email: "", company: "", role: "", industry: "", sub_industry: "", source: "manual", website: "", business_description: "" });
      loadData();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const dismissAlert = async (leadId) => {
    try {
      await api.post("/ops/alerts/dismiss", { lead_id: leadId });
      setAlerts(prev => prev.filter(a => a.lead_id !== leadId));
    } catch (err) { console.error(err); }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.name || !inviteForm.email) return;
    setInviting(true);
    try {
      const { data } = await api.post("/ops/invite", inviteForm);
      setInviteSuccess(`Invite sent to ${inviteForm.email}`);
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteForm({ name: "", email: "", company: "", website: "", industry: "", payment_required: true, personal_message: "", package: "ultimate" });
        setInviteSuccess("");
        loadData();
      }, 2000);
    } catch (err) { console.error(err); }
    finally { setInviting(false); }
  };

  const startReschedule = async (booking) => {
    setEditingBooking(booking);
    setRescheduleForm({ date: booking.date, time_slot: booking.time_slot });
    try {
      const { data } = await api.get(`/booking/available-slots?date=${booking.date}&exclude_booking_id=${booking.booking_id}`);
      setRescheduleSlots(data.slots || []);
    } catch (e) { console.error(e); }
  };

  const loadSlotsForDate = async (date) => {
    setRescheduleForm(f => ({ ...f, date, time_slot: "" }));
    try {
      const { data } = await api.get(`/booking/available-slots?date=${date}&exclude_booking_id=${editingBooking?.booking_id || ""}`);
      setRescheduleSlots(data.slots || []);
    } catch (e) { console.error(e); }
  };

  const saveReschedule = async () => {
    if (!editingBooking || !rescheduleForm.date || !rescheduleForm.time_slot) return;
    try {
      await api.put(`/booking/${editingBooking.booking_id}`, rescheduleForm);
      setEditingBooking(null);
      loadBookings();
    } catch (err) { console.error(err); }
  };

  const cancelBooking = async (bookingId) => {
    try {
      await api.delete(`/booking/${bookingId}`);
      loadBookings();
    } catch (err) { console.error(err); }
  };

  const getStatusColor = (s) => {
    const m = { new: "bg-blue-50 text-blue-700", contacted: "bg-yellow-50 text-yellow-700", booked: "bg-indigo-50 text-indigo-700", qualified: "bg-emerald-50 text-emerald-700", proposal: "bg-purple-50 text-purple-700", closed_won: "bg-green-100 text-green-800", closed_lost: "bg-red-50 text-red-700" };
    return m[s] || "bg-slate-50 text-slate-700";
  };

  if (section === "detail" && selectedLeadId) {
    return <OpsLeadDetail leadId={selectedLeadId} onBack={() => { setSection("pipeline"); setSelectedLeadId(null); loadData(); }} />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="ops-panel">
      {/* Sidebar */}
      <aside className="w-56 bg-[#0F172A] text-white flex-shrink-0 flex flex-col sticky top-0 h-screen" data-testid="ops-sidebar">
        <div className="p-5 border-b border-slate-800">
          <img src="/stansbury-logo-white.png" alt="Stansbury AI" className="h-11 mb-2" />
          <div className="text-[10px] font-['JetBrains_Mono'] text-slate-500 uppercase tracking-wider">Operations</div>
        </div>
        <nav className="flex-1 py-3 px-3 space-y-0.5">
          {NAV_ITEMS.map(item => (
            <button key={item.id} onClick={() => setSection(item.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-['IBM_Plex_Sans'] transition-colors rounded-sm ${section === item.id ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}
              data-testid={`ops-nav-${item.id}`}>
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              {item.label}
              {item.id === "dashboard" && alerts.length > 0 && (
                <span className="ml-auto text-[10px] bg-red-500 text-white px-1.5 py-0.5 font-['JetBrains_Mono']">{alerts.length}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-2.5 mb-3 px-1">
            <div className="w-7 h-7 bg-slate-700 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-['JetBrains_Mono'] text-slate-300 uppercase">{user?.email?.charAt(0) || "A"}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-['IBM_Plex_Sans'] text-slate-300 truncate">{user?.name || "Admin"}</p>
              <p className="text-[10px] font-['IBM_Plex_Sans'] text-slate-600 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => navigate("/dashboard")} className="flex-1 flex items-center justify-center gap-1.5 text-[10px] font-['Outfit'] text-slate-500 hover:text-slate-300 hover:bg-white/5 py-1.5 transition-colors" data-testid="ops-user-view-btn">
              <Eye className="w-3 h-3" />Client View
            </button>
            <button onClick={async () => { await logout(); navigate("/"); }} className="flex-1 flex items-center justify-center gap-1.5 text-[10px] font-['Outfit'] text-slate-500 hover:text-red-400 hover:bg-white/5 py-1.5 transition-colors" data-testid="ops-logout">
              <LogOut className="w-3 h-3" />Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen overflow-auto">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6">

          {/* ═══ DASHBOARD ═══ */}
          {section === "dashboard" && (
            <div data-testid="ops-dashboard-section">
              <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A] mb-6">Command Center</h1>

              {/* Alerts */}
              <DecayAlerts alerts={alerts} openLead={openLead} dismissAlert={dismissAlert} />

              {/* KPIs */}
              {dashboard && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="ops-kpis">
                  {[
                    { label: "Leads", value: dashboard.total_leads, color: "" },
                    { label: "Pipeline", value: `$${(dashboard.total_deal_value || 0).toLocaleString()}`, color: "" },
                    { label: "Revenue", value: `$${(dashboard.total_revenue || 0).toLocaleString()}`, color: "text-emerald-600" },
                    { label: "Won", value: dashboard.pipeline?.closed_won || 0, color: "" },
                    { label: "Win Rate", value: `${dashboard.conversion_rate}%`, color: "" },
                  ].map(k => (
                    <div key={k.label} className="bg-white border border-slate-200 p-4">
                      <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-1">{k.label}</p>
                      <div className={`text-2xl font-['Outfit'] font-light ${k.color || "text-[#0F172A]"}`}>{k.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Two-column: Upcoming Bookings + Recent Submissions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200 p-5" data-testid="upcoming-bookings-widget">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] flex items-center gap-2"><Calendar className="w-4 h-4 text-[#0033CC]" />Upcoming Calls</h3>
                    <button onClick={() => setSection("bookings")} className="text-xs text-[#0033CC] font-['Outfit'] hover:underline">View all</button>
                  </div>
                  {(dashboard?.upcoming_bookings || []).length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No upcoming calls</p> : (
                    <div className="space-y-2">
                      {(dashboard?.upcoming_bookings || []).slice(0, 5).map(b => (
                        <div key={b.booking_id} onClick={() => setSection("bookings")} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0 cursor-pointer hover:bg-slate-50 -mx-2 px-2 transition-colors" data-testid={`dash-booking-${b.booking_id}`}>
                          <div>
                            <span className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A]">{b.name}</span>
                            <span className="text-xs text-slate-400 ml-2">{b.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs font-['JetBrains_Mono'] text-slate-500">{b.date} {b.time_slot}</div>
                            <ArrowRight className="w-3 h-3 text-slate-300" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-white border border-slate-200 p-5" data-testid="recent-submissions-widget">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] flex items-center gap-2"><FileText className="w-4 h-4 text-[#0033CC]" />Recent Submissions</h3>
                    <button onClick={() => setSection("submissions")} className="text-xs text-[#0033CC] font-['Outfit'] hover:underline">View all</button>
                  </div>
                  {(dashboard?.recent_submissions || []).length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No submissions yet</p> : (
                    <div className="space-y-2">
                      {(dashboard?.recent_submissions || []).map(s => (
                        <div key={s.audit_id} onClick={() => { setSection("submissions"); setTimeout(() => loadSubDetail(s.audit_id), 100); }} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0 cursor-pointer hover:bg-slate-50 -mx-2 px-2 transition-colors" data-testid={`dash-sub-${s.audit_id}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A]">{s.company || "Untitled"}</span>
                            <span className={`text-xs font-['JetBrains_Mono'] px-1.5 py-0.5 ${s.status === "completed" ? "bg-blue-50 text-blue-700" : s.status === "report_generated" ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-500"}`}>{formatStatus(s.status)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {s.score && <span className="text-sm font-['JetBrains_Mono'] text-[#0033CC]">{s.score}</span>}
                            <ArrowRight className="w-3 h-3 text-slate-300" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ═══ PIPELINE ═══ */}
          {section === "pipeline" && (
            <div data-testid="ops-pipeline-section">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A]">Pipeline</h1>
                <div className="flex gap-2">
                  <Button onClick={() => setShowInviteModal(true)} variant="outline" className="border-[#0033CC] text-[#0033CC] hover:bg-[#0033CC] hover:text-white rounded-none px-4 h-9 text-sm font-['Outfit'] transition-colors" data-testid="ops-invite-client-btn"><Mail className="w-3.5 h-3.5 mr-1.5" />Invite Client</Button>
                  <Button onClick={() => setShowCreateModal(true)} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-4 h-9 text-sm font-['Outfit']" data-testid="ops-create-lead-btn"><Plus className="w-3.5 h-3.5 mr-1.5" />New Lead</Button>
                </div>
              </div>
              <OpsPipeline leads={leads} onStatusChange={handleStatusChange} onOpenLead={openLead} loading={loading} />
            </div>
          )}

          {/* ═══ LEADS LIST ═══ */}
          {section === "leads" && (
            <div data-testid="ops-leads-section">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A]">All Leads</h1>
                <Button onClick={() => setShowCreateModal(true)} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-4 h-9 text-sm font-['Outfit']"><Plus className="w-3.5 h-3.5 mr-1.5" />New Lead</Button>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-56 h-9 rounded-none border-slate-300 text-sm" data-testid="ops-search" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40 h-9 rounded-none border-slate-300 text-sm" data-testid="ops-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {PIPELINE_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="ops-leads-table">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b border-slate-200">
                    {["Name", "Company", "Status", "Deal Score", "Blueprint Score", "Value", "Source", ""].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-['Outfit'] uppercase tracking-wider text-slate-500">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {leads.map(l => (
                      <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openLead(l.id)}>
                        <td className="px-4 py-3"><div className="font-['IBM_Plex_Sans'] font-medium text-[#0F172A]">{l.name}</div><div className="text-xs text-slate-400">{l.email}</div></td>
                        <td className="px-4 py-3 font-['IBM_Plex_Sans'] text-slate-600">{l.company || "—"}</td>
                        <td className="px-4 py-3"><span className={`text-xs font-['JetBrains_Mono'] px-2 py-0.5 ${getStatusColor(l.status)}`}>{STATUS_LABELS[l.status] || l.status}</span></td>
                        <td className="px-4 py-3"><div className="flex items-center gap-1.5">{l.deal_score >= 60 && <Flame className="w-3 h-3 text-red-500" />}<span className={`text-sm font-['JetBrains_Mono'] font-bold ${l.deal_score >= 60 ? "text-red-600" : l.deal_score >= 40 ? "text-amber-600" : "text-slate-500"}`}>{l.deal_score || 0}</span></div></td>
                        <td className="px-4 py-3 font-['JetBrains_Mono'] text-sm">{l.score || "—"}</td>
                        <td className="px-4 py-3 font-['JetBrains_Mono'] text-sm">{l.deal_value ? `$${l.deal_value.toLocaleString()}` : "—"}</td>
                        <td className="px-4 py-3 text-xs text-slate-400 capitalize">{(l.source || "").replace(/_/g, " ")}</td>
                        <td className="px-4 py-3"><ArrowRight className="w-4 h-4 text-slate-300" /></td>
                      </tr>
                    ))}
                    {leads.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No leads found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ SUBMISSIONS ═══ */}
          {section === "submissions" && (
            <div data-testid="ops-submissions-section">
              {selectedSubmission && subDetail ? (
                /* ── Submission Detail View ── */
                <div data-testid="submission-detail-view">
                  <button onClick={() => { setSelectedSubmission(null); setSubDetail(null); }} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 font-['Outfit'] mb-6" data-testid="sub-back-to-list">
                    <ArrowLeft className="w-4 h-4" /> Back to submissions
                  </button>

                  {/* Header */}
                  <div className="bg-white border border-slate-200 p-6 mb-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h2 className="text-xl font-['Outfit'] font-medium text-[#0F172A]">{subDetail.company_info?.company_name || "Untitled Blueprint"}</h2>
                        <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">{subDetail.user?.email} {subDetail.user?.name ? `(${subDetail.user.name})` : ""}</p>
                        {subDetail.company_info?.website && <p className="text-xs text-[#0033CC] font-['IBM_Plex_Sans'] mt-1">{subDetail.company_info.website}</p>}
                        {subDetail.company_info?.business_description && <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mt-1 max-w-xl">{subDetail.company_info.business_description}</p>}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs font-['IBM_Plex_Sans'] text-slate-400 capitalize">{subDetail.company_info?.industry?.replace(/_/g, " ")}</span>
                          <span className="text-xs font-['IBM_Plex_Sans'] text-slate-400">{subDetail.company_info?.company_size} employees</span>
                          <span className="text-xs font-['IBM_Plex_Sans'] text-slate-400">{subDetail.company_info?.annual_revenue}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <span className={`text-xs font-['JetBrains_Mono'] px-2 py-0.5 ${subDetail.status === "report_generated" ? "bg-green-50 text-green-700" : subDetail.status === "completed" ? "bg-blue-50 text-blue-700" : "bg-slate-50 text-slate-500"}`}>{formatStatus(subDetail.status)}</span>
                        </div>
                        <div className="text-center">
                          <span className={`text-xs font-['JetBrains_Mono'] px-2 py-0.5 ${subDetail.payment_status === "paid" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{formatStatus(subDetail.payment_status)}</span>
                        </div>
                        {subDetail.scores?.overall != null && (
                          <div className="text-center px-3">
                            <div className="text-2xl font-['Outfit'] font-light text-[#0033CC]">{subDetail.scores.overall}</div>
                            <p className="text-xs text-slate-400 font-['JetBrains_Mono']">/100</p>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Linked Lead */}
                    {subDetail.linked_lead ? (
                      <div className="mt-4 p-3 bg-[#E5EDFF] border border-[#0033CC]/20 flex items-center justify-between" data-testid="linked-lead-banner">
                        <div className="flex items-center gap-2">
                          <Link2 className="w-4 h-4 text-[#0033CC]" />
                          <span className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A]">Linked to lead: <strong>{subDetail.linked_lead.name}</strong></span>
                          <span className={`text-xs font-['JetBrains_Mono'] px-1.5 py-0.5 ${getStatusColor(subDetail.linked_lead.status)}`}>{STATUS_LABELS[subDetail.linked_lead.status] || subDetail.linked_lead.status}</span>
                          {subDetail.linked_lead.deal_value > 0 && <span className="text-xs font-['JetBrains_Mono'] text-emerald-600">${subDetail.linked_lead.deal_value.toLocaleString()}</span>}
                        </div>
                        <Button size="sm" onClick={() => openLead(subDetail.linked_lead.id)} className="bg-[#0033CC] text-white rounded-none h-7 text-xs font-['Outfit']" data-testid="go-to-lead-btn">Open Lead <ArrowRight className="w-3 h-3 ml-1" /></Button>
                      </div>
                    ) : (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        <span className="text-sm text-amber-700 font-['IBM_Plex_Sans']">No lead linked to this submission</span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Scores */}
                    {subDetail.scores && (
                      <div className="bg-white border border-slate-200 p-6" data-testid="sub-scores">
                        <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Discovery Scores</h3>
                        <div className="flex items-center gap-4 mb-4">
                          <div className="text-3xl font-['Outfit'] font-light text-[#0033CC]">{subDetail.scores.overall}</div>
                          <div className="text-sm text-slate-500">/100 — {subDetail.scores.tier}</div>
                        </div>
                        <div className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mb-3">Revenue Leak: {subDetail.scores.revenue_leak_severity} | Op. Drag: {subDetail.scores.operational_drag}</div>
                        <div className="space-y-2">
                          {Object.entries(subDetail.scores.sections || {}).filter(([k]) => !["acquire","convert","operate","expand","defend","readiness"].includes(k)).map(([k, v]) => (
                            <div key={k} className="flex items-center gap-3">
                              <span className="text-xs text-slate-500 w-32 capitalize font-['IBM_Plex_Sans'] truncate">{k.replace(/_/g, " ")}</span>
                              <div className="flex-1 h-1.5 bg-slate-100"><div className="h-full bg-[#0033CC]" style={{ width: `${v.percentage}%` }} /></div>
                              <span className="text-xs font-['JetBrains_Mono'] w-10 text-right">{v.percentage}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Answers */}
                    <div className="bg-white border border-slate-200 p-6" data-testid="sub-answers">
                      <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Blueprint Answers</h3>
                      <div>
                        {Object.entries(subDetail.answers || {}).map(([section, ans]) => (
                          <div key={section} className="mb-4">
                            <h4 className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-2">{section.replace(/_/g, " ")}</h4>
                            {Object.entries(ans || {}).map(([qId, val]) => (
                              <div key={qId} className="flex justify-between text-xs text-slate-600 font-['IBM_Plex_Sans'] py-1 border-b border-slate-50">
                                <span className="capitalize">{qId.replace(/_/g, " ")}</span>
                                <span className="font-['JetBrains_Mono'] text-slate-800">{String(val)}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                        {Object.keys(subDetail.answers || {}).length === 0 && <p className="text-xs text-slate-400">No answers recorded</p>}
                      </div>
                    </div>

                    {/* Report */}
                    {subDetail.report && (
                      <div className="lg:col-span-2 bg-white border border-slate-200 p-6" data-testid="sub-report">
                        <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Transformation Blueprint</h3>
                        {(subDetail.report.executive_snapshot?.company_overview || subDetail.report.executive_summary) && (
                          <div className="mb-4">
                            <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Executive Snapshot</h4>
                            <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">{subDetail.report.executive_snapshot?.company_overview || (typeof subDetail.report.executive_summary === "string" ? subDetail.report.executive_summary : "")}</p>
                          </div>
                        )}
                        {subDetail.report.revenue_leak_analysis?.total_estimated_leakage && (
                          <div className="mb-4 bg-red-50 border border-red-200 p-4">
                            <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-red-600 mb-1">Revenue Leakage</h4>
                            <p className="text-lg font-['Outfit'] font-medium text-red-600">{subDetail.report.revenue_leak_analysis.total_estimated_leakage}</p>
                          </div>
                        )}
                        {subDetail.report.opportunity_matrix?.opportunities && (
                          <div className="mb-4">
                            <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-2">AI Opportunities ({subDetail.report.opportunity_matrix.opportunities.length})</h4>
                            <div className="space-y-1.5">
                              {subDetail.report.opportunity_matrix.opportunities.slice(0, 5).map((o, i) => (
                                <div key={`om-${i}`} className="flex items-center gap-2 text-xs py-1 border-b border-slate-50">
                                  <span className={`font-['JetBrains_Mono'] px-1.5 py-0.5 text-white ${o.priority === "P1" ? "bg-[#0033CC]" : "bg-slate-400"}`}>{o.priority}</span>
                                  <span className="font-['Outfit'] font-medium text-[#0F172A] flex-1">{o.initiative}</span>
                                  <span className="font-['JetBrains_Mono'] text-emerald-600">{o.revenue_impact}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {subDetail.report.roi_model?.economics && (
                          <div className="bg-[#0F172A] p-4">
                            <h4 className="text-xs font-['JetBrains_Mono'] uppercase text-[#818CF8] mb-2">ROI Summary</h4>
                            <div className="flex gap-4">
                              {Object.entries(subDetail.report.roi_model.economics).slice(0, 3).map(([k, v]) => (
                                <div key={k} className="text-center flex-1">
                                  <p className="text-[10px] text-slate-400">{k.replace(/_/g, " ")}</p>
                                  <p className="text-base font-['Outfit'] font-light text-white">{String(v)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* ── Submissions List View ── */
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A]">Blueprint Submissions</h1>
                    <Select value={subFilter} onValueChange={setSubFilter}>
                      <SelectTrigger className="w-48 h-9 rounded-none border-slate-300 text-sm" data-testid="sub-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="report_generated">Report Generated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <SubmissionsTable submissions={submissions} loadSubDetail={loadSubDetail} openLead={openLead} formatStatus={formatStatus} />
                </div>
              )}
            </div>
          )}

          {/* ═══ BOOKINGS ═══ */}
          {section === "bookings" && (
            <div data-testid="ops-bookings-section">
              <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A] mb-6">Call Bookings</h1>
              <BookingsTable bookings={bookings} startReschedule={startReschedule} cancelBooking={cancelBooking} />
            </div>
          )}

          {/* ═══ ANALYTICS ═══ */}
          {section === "analytics" && <OpsAnalytics onOpenLead={openLead} />}

          {/* ═══ SALES MATERIAL ═══ */}
          {section === "sales-material" && <OpsSalesMaterial />}
          {section === "insights" && <OpsInsights />}

        </div>
      </main>

      {/* Create Lead Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white border border-slate-200 p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="create-lead-modal">
            <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-4">Create New Lead</h3>
            <div className="space-y-3">
              <Input value={newLead.name} onChange={e => setNewLead({ ...newLead, name: e.target.value })} placeholder="Full name *" className="rounded-none border-slate-300" data-testid="new-lead-name" />
              <Input value={newLead.email} onChange={e => setNewLead({ ...newLead, email: e.target.value })} placeholder="Email *" type="email" className="rounded-none border-slate-300" data-testid="new-lead-email" />
              <Input value={newLead.company} onChange={e => setNewLead({ ...newLead, company: e.target.value })} placeholder="Company" className="rounded-none border-slate-300" data-testid="new-lead-company" />
              <Input value={newLead.role} onChange={e => setNewLead({ ...newLead, role: e.target.value })} placeholder="Role / Title" className="rounded-none border-slate-300" data-testid="new-lead-role" />
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Industry</Label>
                <Select value={newLead.industry} onValueChange={v => setNewLead({ ...newLead, industry: v, sub_industry: "" })}>
                  <SelectTrigger className="rounded-none border-slate-300" data-testid="new-lead-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(ind => <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {newLead.industry && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Sector</Label>
                  <Select value={newLead.sub_industry} onValueChange={v => setNewLead({ ...newLead, sub_industry: v })}>
                    <SelectTrigger className="rounded-none border-slate-300" data-testid="new-lead-sub-industry"><SelectValue placeholder="Select sector" /></SelectTrigger>
                    <SelectContent>
                      {(INDUSTRIES.find(i => i.value === newLead.industry)?.subs || []).map(sub => <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Input value={newLead.website} onChange={e => setNewLead({ ...newLead, website: e.target.value })} placeholder="Website URL" type="url" className="rounded-none border-slate-300" data-testid="new-lead-website" />
              <Textarea value={newLead.business_description} onChange={e => setNewLead({ ...newLead, business_description: e.target.value })} placeholder="What does this company do? (2 sentences min)" className="rounded-none border-slate-300 min-h-[60px]" data-testid="new-lead-description" />
              <Select value={newLead.source} onValueChange={v => setNewLead({ ...newLead, source: v })}>
                <SelectTrigger className="rounded-none border-slate-300" data-testid="new-lead-source"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="manual">Manual</SelectItem><SelectItem value="inbound">Inbound</SelectItem><SelectItem value="audit">Blueprint</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1 rounded-none font-['Outfit']">Cancel</Button>
              <Button onClick={handleCreate} disabled={creating || !newLead.name || !newLead.email} className="flex-1 bg-[#0033CC] text-white hover:bg-[#002299] rounded-none font-['Outfit']" data-testid="create-lead-submit">{creating ? "Creating..." : "Create Lead"}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {editingBooking && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditingBooking(null)}>
          <div className="bg-white border border-slate-200 p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="reschedule-modal">
            <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-2">Reschedule Booking</h3>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-4">{editingBooking.name} &middot; Currently {editingBooking.date} at {editingBooking.time_slot}</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">New Date</label>
                <Input type="date" value={rescheduleForm.date} onChange={e => loadSlotsForDate(e.target.value)} className="rounded-none border-slate-300" data-testid="reschedule-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Available Slots (30-min buffer enforced)</label>
                {rescheduleSlots.length === 0 ? <p className="text-xs text-slate-400">No available slots for this date</p> : (
                  <div className="grid grid-cols-4 gap-1.5" data-testid="reschedule-slots">
                    {rescheduleSlots.map(s => (
                      <button key={s} onClick={() => setRescheduleForm(f => ({ ...f, time_slot: s }))}
                        className={`p-2 border text-xs font-['JetBrains_Mono'] transition-all ${rescheduleForm.time_slot === s ? "border-[#0033CC] bg-[#E5EDFF] text-[#0033CC]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                        data-testid={`reschedule-slot-${s}`}>{s}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setEditingBooking(null)} className="flex-1 rounded-none font-['Outfit']">Cancel</Button>
              <Button onClick={saveReschedule} disabled={!rescheduleForm.date || !rescheduleForm.time_slot} className="flex-1 bg-[#0033CC] text-white hover:bg-[#002299] rounded-none font-['Outfit']" data-testid="reschedule-confirm">Confirm Reschedule</Button>
            </div>
          </div>
        </div>
      )}
      {/* Invite Client Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowInviteModal(false)}>
          <div className="bg-white border border-slate-200 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="invite-modal">
            <div className="bg-[#0F172A] p-5">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-5 h-5 text-[#818CF8]" />
                <h3 className="text-lg font-['Outfit'] font-medium text-white">Invite Client to Blueprint</h3>
              </div>
              <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">Send a personalized invite with account creation and onboarding.</p>
            </div>
            {inviteSuccess ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 bg-emerald-50 flex items-center justify-center mx-auto mb-3"><Send className="w-6 h-6 text-emerald-600" /></div>
                <p className="text-sm font-['Outfit'] font-medium text-emerald-800">{inviteSuccess}</p>
              </div>
            ) : (
              <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-slate-500">Full Name *</Label><Input value={inviteForm.name} onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="Jamie Smith" className="mt-1 rounded-none border-slate-300 h-9 text-sm" data-testid="invite-name" /></div>
                  <div><Label className="text-xs text-slate-500">Email *</Label><Input type="email" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="jamie@company.com" className="mt-1 rounded-none border-slate-300 h-9 text-sm" data-testid="invite-email" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-slate-500">Company</Label><Input value={inviteForm.company} onChange={e => setInviteForm({ ...inviteForm, company: e.target.value })} placeholder="Acme Corp" className="mt-1 rounded-none border-slate-300 h-9 text-sm" data-testid="invite-company" /></div>
                  <div><Label className="text-xs text-slate-500">Website</Label><Input value={inviteForm.website} onChange={e => setInviteForm({ ...inviteForm, website: e.target.value })} placeholder="https://..." className="mt-1 rounded-none border-slate-300 h-9 text-sm" data-testid="invite-website" /></div>
                </div>
                <div><Label className="text-xs text-slate-500">Industry</Label>
                  <Select value={inviteForm.industry} onValueChange={v => setInviteForm({ ...inviteForm, industry: v })}>
                    <SelectTrigger className="mt-1 rounded-none border-slate-300 h-9 text-sm"><SelectValue placeholder="Select industry" /></SelectTrigger>
                    <SelectContent>
                      {["Agency", "SaaS", "Professional Services", "E-Commerce", "Real Estate", "Healthcare", "Financial Services", "Media", "Education", "Other"].map(ind => (
                        <SelectItem key={ind} value={ind.toLowerCase().replace(/\s/g, "_")}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Payment Toggle */}
                <div className="bg-slate-50 border border-slate-200 p-4">
                  <Label className="text-xs text-slate-500 mb-2 block">Payment Status</Label>
                  <div className="flex gap-2">
                    <button onClick={() => setInviteForm({ ...inviteForm, payment_required: true })}
                      className={`flex-1 p-3 border text-sm font-['IBM_Plex_Sans'] text-center transition-all ${inviteForm.payment_required ? "border-[#0033CC] bg-[#E5EDFF] text-[#0033CC]" : "border-slate-200 text-slate-500"}`} data-testid="invite-payment-required">
                      Payment Required<br /><span className="text-[10px]">Client pays via Stripe</span>
                    </button>
                    <button onClick={() => setInviteForm({ ...inviteForm, payment_required: false })}
                      className={`flex-1 p-3 border text-sm font-['IBM_Plex_Sans'] text-center transition-all ${!inviteForm.payment_required ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`} data-testid="invite-payment-waived">
                      Payment Waived<br /><span className="text-[10px]">Paid offline / comped</span>
                    </button>
                  </div>
                </div>
                <div><Label className="text-xs text-slate-500">Personal Message (Optional)</Label>
                  <Textarea value={inviteForm.personal_message} onChange={e => setInviteForm({ ...inviteForm, personal_message: e.target.value })} placeholder="Add a personal note that will appear in the invite email..." className="mt-1 rounded-none border-slate-300 text-sm min-h-[60px]" data-testid="invite-message" />
                </div>
              </div>
            )}
            {!inviteSuccess && (
              <div className="p-5 border-t border-slate-200 flex gap-3">
                <Button variant="outline" onClick={() => setShowInviteModal(false)} className="flex-1 rounded-none font-['Outfit']">Cancel</Button>
                <Button onClick={handleSendInvite} disabled={inviting || !inviteForm.name || !inviteForm.email} className="flex-1 bg-[#0033CC] text-white hover:bg-[#002299] rounded-none font-['Outfit']" data-testid="invite-send-btn">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Send Invite</>}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Close Reason Modal */}
      {closeModal && (
        <CloseReasonModal
          isWin={closeModal.isWin}
          leadName={closeModal.leadName}
          company={closeModal.company}
          onConfirm={handleCloseConfirm}
          onCancel={() => setCloseModal(null)}
        />
      )}
    </div>
  );
}
